import ApiServer from '@/utils/api/ApiServer.js'
import { EudrBatchStatus } from '@/utils/Enumerations.js'

export default {
  async fetchBatches (context, destinationAccountId) {
    try {
      context.commit('setLoading', true)
      const url = ApiServer.urlFor('eudr', { version: 1 })
      const response = await ApiServer.get(url)
      const reports = destinationAccountId != null
        ? response.data.filter(r => r.destinationAccountId === destinationAccountId)
        : response.data
      context.commit('setEudrBatches', reports)
      return reports
    } finally {
      context.commit('setLoading', false)
    }
  },

  async fetchBatchById (_, id) {
    const url = ApiServer.urlFor(`eudr/${id}`, { version: 1 })
    const response = await ApiServer.get(url)
    return response.data
  },

  // The server returns reports whose window overlaps [startTime, endTime], not only those fully inside it.
  async fetchAccountBatches (_, { accountId, startDate, endDate }) {
    const url = ApiServer.urlFor(['accounts', accountId, 'eudr'], { params: { startTime: startDate, endTime: endDate }, version: 1 })
    const response = await ApiServer.get(url)
    return response.data
  },

  // Internal accounts with reportable production, external reports already uploaded, and suppliers still missing one.
  async fetchManagerOverview (_, { startDate, endDate }) {
    const url = ApiServer.urlFor('eudr/overview', { params: { startTime: startDate, endTime: endDate }, version: 1 })
    const response = await ApiServer.get(url)
    return response.data
  },

  async deleteBatch (_, id) {
    const url = ApiServer.urlFor(`eudr/${id}`, { version: 1 })
    const response = await ApiServer.delete(url)
    return response.data
  },

  async uploadFile (_, { accountId, startDate, endDate, geojson, sourceFileName }) {
    const url = ApiServer.urlFor('eudr/upload', { version: 1 })
    const response = await ApiServer.post(url, { accountId, startDate, endDate, geojson, sourceFileName })
    return response.data
  },

  // Composite of internal accounts (harvested inline server-side) and/or external uploaded reports.
  async createComposite (_, { label, startDate, endDate, sourceBatchIds, internalAccountIds }) {
    const url = ApiServer.urlFor('eudr/composite', { version: 1 })
    const response = await ApiServer.post(url, { label, startDate, endDate, sourceBatchIds, internalAccountIds })
    return response.data
  },

  // The server dictates the canonical blob path; the client writes there, then records it via saveValidation/finalize.
  async fetchWriteSas (_, { id, artifact }) {
    const url = ApiServer.urlFor(['eudr', id, 'writesas'], { params: { artifact }, version: 1 })
    const response = await ApiServer.get(url)
    return response.data
  },

  // Record a validation save. The validated blob must already be uploaded (via fetchWriteSas) or the server 400s.
  async saveValidation (_, { id, excludedFeatureCount }) {
    const url = ApiServer.urlFor(['eudr', id], { version: 1 })
    const response = await ApiServer.put(url, { status: EudrBatchStatus.Validated.value, excludedFeatureCount })
    return response.data
  },

  // Discard validation (and any finalized artifact); the batch returns to its post-generation status.
  async resetValidation (_, id) {
    const url = ApiServer.urlFor(['eudr', id, 'validation'], { version: 1 })
    const response = await ApiServer.delete(url)
    return response.data
  },

  // Record a finalize. The sanitized (EU submission) blob must already be uploaded via fetchWriteSas.
  async finalizeBatch (_, id) {
    const url = ApiServer.urlFor(['eudr', id], { version: 1 })
    const response = await ApiServer.put(url, { status: EudrBatchStatus.Finalized.value })
    return response.data
  },

  // External byproduct buyers within the composite's window — the accounts its share file can be emailed to.
  async fetchShareCandidates (_, id) {
    const url = ApiServer.urlFor(['eudr', id, 'sharecandidates'], { version: 1 })
    const response = await ApiServer.get(url)
    return response.data
  },

  // Share file must already be uploaded (fetchWriteSas artifact=share). Returns { queuedEmailCount, skippedAccounts }.
  // artifact: 'share' (rich flavor, uploaded just beforehand) or 'sanitized' (the finalized EU submission file).
  async sendEudrShareFile (_, { id, artifact, accountIds }) {
    const url = ApiServer.urlFor(['eudr', id, 'send'], { version: 1 })
    const response = await ApiServer.post(url, { artifact, accountIds })
    return response.data
  },

  // Email a supplier's EUDR contact(s) asking for their geolocation file covering the period.
  async requestEudrFile (_, { accountId, startDate, endDate }) {
    const url = ApiServer.urlFor('eudr/filerequest', { version: 1 })
    const response = await ApiServer.post(url, { accountId, startDate, endDate })
    return response.data
  }
}
